import '@/assets/styles/components/modal.styles.scss';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import clsx from 'clsx';
import { ModalProps } from './modal.types';

export const Modal = ({
	isOpen,
	onClose,
	children,
	hasHeader = false,
	title,
	size = 'md',
	variant = 'default',
	showCloseButton = false,
	closeOnClickOutside = true,
	closeOnEsc = true,
	preventScroll = true,
	className,
	contentClassName,
	overlayClassName,
	headerClassName,
	bodyClassName,
	hideOverlay = false,
	animationDuration = 300,
	customXIcon
}: ModalProps) => {
	const modalRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const scrollPosition = useRef(0);

	useEffect(() => {
		const handleEsc = (e: KeyboardEvent) => {
			if (closeOnEsc && e.key === 'Escape') {
				onClose();
			}
		};

		const handleClickOutside = (e: MouseEvent) => {
			if (closeOnClickOutside && modalRef.current && contentRef.current && !contentRef.current.contains(e.target as Node)) {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener('keydown', handleEsc);
			document.addEventListener('mousedown', handleClickOutside);

			if (preventScroll) {
				scrollPosition.current = window.scrollY;
				document.body.style.position = 'fixed';
				document.body.style.top = `-${scrollPosition.current}px`;
				document.body.style.width = '100%';
			}
		}

		return () => {
			document.removeEventListener('keydown', handleEsc);
			document.removeEventListener('mousedown', handleClickOutside);

			if (preventScroll) {
				document.body.style.position = '';
				document.body.style.top = '';
				document.body.style.width = '';
				window.scrollTo(0, scrollPosition.current);
			}
		};
	}, [isOpen, closeOnEsc, closeOnClickOutside, onClose, preventScroll]);

	if (!isOpen) return null;

	return createPortal(
		<div
			ref={modalRef}
			className={clsx('modal-wrapper', `modal-variant--${variant}`, className)}
			style={
				{
					'--animation-duration': `${animationDuration}ms`
				} as React.CSSProperties
			}>
			{!hideOverlay && (
				<div
					className={clsx('modal-overlay', overlayClassName)}
					aria-hidden="true"
				/>
			)}

			<div
				ref={contentRef}
				className={clsx('modal-content', `modal-size--${size}`, { 'has-header': hasHeader }, contentClassName)}
				role="dialog"
				aria-modal="true"
				aria-labelledby={title ? 'modal-title' : undefined}>
				{hasHeader && (
					<div className={clsx('modal-header', headerClassName)}>
						{title && (
							<h2
								id="modal-title"
								className="modal-title">
								{title}
							</h2>
						)}
						{showCloseButton && (
							<button
								type="button"
								className="modal-close"
								onClick={onClose}
								aria-label="Close modal">
								{customXIcon ? customXIcon : <X size={20} />}
							</button>
						)}
					</div>
				)}

				{!hasHeader && showCloseButton && (
					<button
						type="button"
						className="modal-close absolute"
						onClick={onClose}
						aria-label="Close modal">
						{customXIcon ? customXIcon : <X size={20} />}
					</button>
				)}

				<div className={clsx('modal-body', bodyClassName)}>{children}</div>
			</div>
		</div>,
		document.body
	);
};

export default Modal;
